import type { FC } from 'react';
import React, { useContext, useState } from 'react';

import { ModalServiceContext } from './../../ricos-context';
import { useBiNext } from './../../_shared/bi-context-provider/bi-context-provider';
import type { PmActionButtonNode, PmLinkButtonNode, PmNodeType } from './../../_shared/pm-node-types';
import { useNodeAttrs } from './../../_shared/use-node-attrs';
import { useClickOutside, usePanelContainerElement } from './../../ui-components';

import type { SettingsTab } from '../constants';
import { buttonsModals, SETTINGS_TAB } from '../constants';
import type { ACTION_BUTTON_TYPE, ButtonPluginEditorConfigCommon } from '../types';
import { LINK_BUTTON_TYPE } from '../types';
import { SettingsModalContent } from './SettingsModalContent';

interface Props {
  nodeId: string;
  modalId: string;
  settings: ButtonPluginEditorConfigCommon;
  type: typeof LINK_BUTTON_TYPE | typeof ACTION_BUTTON_TYPE;
  activeTab?: SettingsTab;
  referenceElement?: HTMLElement;
}

const ButtonSettingsModal: FC<Props> = ({
  nodeId,
  settings,
  type,
  modalId,
  activeTab: tab,
  referenceElement,
}) => {
  const modalService = useContext(ModalServiceContext) || {};

  const containerEl = usePanelContainerElement();

  const nodeType: typeof PmNodeType.LINK_BUTTON | typeof PmNodeType.ACTION_BUTTON =
    type === LINK_BUTTON_TYPE ? 'LINK_BUTTON' : 'ACTION_BUTTON';

  const logBi = useBiNext({
    modalId: buttonsModals.settings[type],
    pluginId: nodeType === 'LINK_BUTTON' ? 'linkButton' : 'actionButton',
    nodeId,
  });

  const { patchPmNodeAttrs, pmNodeAttrs } = useNodeAttrs(nodeId, nodeType);

  const [activeTab, setActiveTab] = useState<SettingsTab>(tab ?? SETTINGS_TAB.SETTINGS);

  const handleOnChange = (data: Partial<(PmLinkButtonNode | PmActionButtonNode)['attrs']>) =>
    patchPmNodeAttrs?.(data);

  const isColorPickerModalOpen = () => modalService.isModalOpen(buttonsModals.colorPicker);

  const closeModal = () => modalService.closeModal(modalId);

  useClickOutside({
    containerRef: { current: containerEl },
    onClickOutside: (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement | null;

      if (referenceElement?.contains(target as Node)) {
        return;
      }

      closeModal();
    },
    disable: isColorPickerModalOpen(),
  });

  return pmNodeAttrs ? (
    <SettingsModalContent
      buttonData={pmNodeAttrs}
      config={settings}
      onClose={closeModal}
      onChange={handleOnChange}
      nodeId={nodeId}
      nodeType={nodeType}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      logBi={logBi}
    />
  ) : null;
};

export default ButtonSettingsModal;
